import { Box } from '@material-ui/core'
import { SimpleTable } from 'components/controls/tables'
import { Button } from 'components/facets'
import { ArticleAttributes } from 'components/facets/ArticleAttributes'
import { ConditionBadge } from 'components/facets/ConditionBadge'
import { LanguageFlag } from 'components/facets/LanguageFlag'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CardLanguage, flagCode, InventoryArticle, isSingle } from 'shared'

export interface StockReviewProps {
  incomingStock: InventoryArticle[]
  idGame: number
  type: 'singles' | 'accessories'
  handleClose: () => void
}

export const StockReviewFull = ({
  incomingStock,
  idGame,
  type,
  handleClose,
}: StockReviewProps): JSX.Element => {
  const { t } = useTranslation()
  const [filteredIncomingStock, setFilteredIncomingStock] = useState(incomingStock)

  useEffect(() => {
    const newFilteredIncomingStock = incomingStock.filter((article) => {
      if (type === 'singles') {
        return isSingle(article.card) && article.card.idGame === idGame
      } else {
        return !isSingle(article.card) && article.card.idGame === idGame
      }
    })
    setFilteredIncomingStock(newFilteredIncomingStock)
  }, [incomingStock])

  type ResultDataItem = InventoryArticle & {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any
  }

  return (
    <>
      <Box className="scrollable">
        <SimpleTable<ResultDataItem>
          maxWidth="1200px"
          options={{ pageSize: 10, rowStyle: { height: '45px' } }}
          columns={[
            // name
            {
              title: t('card.name'),
              defaultSort: 'desc',
              field: 'card.name',
              render: (article) => <Box width="300px">{article.card.name}</Box>,
            },
            // expansion
            {
              title: t('card.expansion'),
              defaultSort: 'desc',
              field: 'card.expansion',
              render: (article) => <Box width="300px">{article.card.expansion}</Box>,
            },
            // language
            {
              title: t('card.language'),
              defaultSort: 'desc',
              field: 'language',
              render: (article) => (
                <Box>
                  <LanguageFlag
                    style={{ padding: '0px 5px' }}
                    height="15"
                    data-testid={`${CardLanguage[article.idLanguage]}-flag`}
                    code={flagCode(article.idLanguage) || 'gg'}
                  />
                </Box>
              ),
            },
            // condition
            {
              title: t('card.condition'),
              defaultSort: 'desc',
              field: 'condition',
              render: (article) => (
                <Box>
                  <ConditionBadge condition={article.condition} />
                </Box>
              ),
            },
            // extras
            {
              title: t('card.extra'),
              defaultSort: 'desc',
              field: 'extra',
              render: (article) => (
                <div>
                  <ArticleAttributes article={article} iconHeight={18} onlyExtraAttributes={true} />
                </div>
              ),
            },
            // quantity
            {
              title: t('card.quantity'),
              defaultSort: 'desc',
              field: 'quantity',
              render: (article) => <Box>{article.quantity}</Box>,
            },
            // comment
            {
              title: t('card.comment'),
              defaultSort: 'desc',
              field: 'comment',
              cellStyle: { textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
              render: (article) => <Box className="text-wrap">{article.comment}</Box>,
            },
          ]}
          data={
            filteredIncomingStock.map((article) => ({
              ...article.toPlainObject(),
            })) as ResultDataItem[]
          }
          title=""
        />
      </Box>
      <div className="w-100 my-3 d-flex justify-content-end">
        <Button variant="contained" onClick={handleClose}>
          {t('common.close')}
        </Button>
      </div>
    </>
  )
}
